import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Heart, ArrowUpRight, Calendar, Gauge, Users, Trash2 } from "lucide-react";
import { fyndanuListingsQuery, formatPrice, formatMil } from "@/lib/vehicles";
import { useFavorites } from "@/hooks/use-favorites";
import { FavoriteButton } from "@/components/FavoriteButton";
import { vehicleSlug } from "@/lib/vehicle-slug";

const BASE = "https://www.akhusbilfritid.se";

export const Route = createFileRoute("/sparade")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  head: () => ({
    meta: [
      { title: "Sparade fordon — AK Husbil & Fritid" },
      { name: "description", content: "Dina sparade husbilar och husvagnar. Fortsätt jämföra och boka visning när du är redo." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${BASE}/sparade` }],
  }),
  component: FavoritesPage,
});

function FavoritesPage() {
  const { data } = useSuspenseQuery(fyndanuListingsQuery);
  const { ids, clear, count } = useFavorites();
  const items = data.items.filter((i) => ids.includes(i.id));

  return (
    <div className="bg-warm">
      <div className="container-edge py-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Dina sparade</p>
            <h1 className="mt-1 font-display text-3xl md:text-5xl">
              Sparade <span className="font-italic-display text-accent">fordon</span>
            </h1>
            <p className="mt-2 text-muted-foreground">
              Sparas lokalt i din webbläsare — ingen inloggning behövs.
            </p>
          </div>
          {count > 0 && (
            <button
              type="button"
              onClick={() => { if (confirm("Rensa alla sparade fordon?")) clear(); }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:bg-secondary"
            >
              <Trash2 size={14} /> Rensa alla
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
            <Heart size={32} className="mx-auto text-muted-foreground" />
            <p className="mt-4 font-display text-2xl">Inga sparade ännu</p>
            <p className="mt-2 text-sm text-muted-foreground">Klicka på hjärtat på ett fordon för att spara det här.</p>
            <Link to="/fordon" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">
              Bläddra fordon <ArrowUpRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const mil = formatMil(item.mileage);
              return (
                <Link
                  key={item.id}
                  to="/fordon/$id"
                  params={{ id: vehicleSlug(item) }}
                  className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                    <img src={item.image} alt={item.title} loading="lazy" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                    <div className="absolute right-3 top-3 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur">
                      {formatPrice(item.price)}
                    </div>
                    <FavoriteButton id={item.id} className="absolute left-3 top-3" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-xl leading-tight line-clamp-2 group-hover:text-accent">{item.title}</h3>
                    <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {item.year && <div className="inline-flex items-center gap-1"><Calendar size={12} /> {item.year}</div>}
                      {mil && <div className="inline-flex items-center gap-1"><Gauge size={12} /> {mil}</div>}
                      {item.sleepingSpots != null && <div className="inline-flex items-center gap-1"><Users size={12} /> {item.sleepingSpots} bäddar</div>}
                    </dl>
                    <span className="mt-auto pt-4 inline-flex items-center gap-1 text-sm font-medium text-accent">
                      Öppna fordon <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
